// src/components/store/SummerCountdown.tsx
"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import gsap from "gsap";
import { ArrowRight, Flame, ChevronLeft, ChevronRight } from "lucide-react";

export interface CustomTextLayer {
  id: string;
  text: string;
  x: number;
  y: number;
  scale: number;
  color: string;
  weight: "normal" | "bold" | "black";
}

export interface BannerData {
  id?: number;
  badge_title?: string;
  title?: string;
  subtitle?: string;
  button_text?: string;
  target_url?: string;
  countdown_end?: string | null;
  background_image?: string | null;
  image?: string | null;            // Left Product Cutout
  secondary_image?: string | null;  // Right Model Cutout
  stage_preset?: string | null;

  left_ox?: number | string | null;
  left_oy?: number | string | null;
  left_scale?: number | string | null;

  right_ox?: number | string | null;
  right_oy?: number | string | null;
  right_scale?: number | string | null;

  text_ox?: number | string | null;
  text_oy?: number | string | null;
  text_scale?: number | string | null;

  timer_ox?: number | string | null;
  timer_oy?: number | string | null;
  timer_scale?: number | string | null;
  timer_gap?: number | string | null;
  timer_pad_x?: number | string | null;
  timer_pad_y?: number | string | null;
  timer_box_bg?: boolean | string | null;

  btn_ox?: number | string | null;
  btn_oy?: number | string | null;
  btn_scale?: number | string | null;
  btn_show?: boolean | string | null;

  custom_texts?: string | CustomTextLayer[] | null;
}

interface SummerCountdownProps {
  banners?: BannerData[];
  banner?: BannerData | null;
}

const PRESET_BACKGROUNDS: Record<string, string> = {
  neon_scifi: "/banners/banner_neon_scifi.png",
  blue_gold_showcase: "/banners/banner_blue_gold_showcase.png",
  botanical_showroom: "/banners/banner_botanical_showroom.png",
  pastel_showroom: "/banners/banner_pastel_showroom.png",
  gold_blue_stage: "/banners/banner_gold_blue_stage.png",
  ecommerce_showcase: "/banners/banner_ecommerce_showcase.png",
  pastel_brush_sale: "/banners/banner_pastel_brush_sale.png",
};

export default function SummerCountdown({ banners, banner }: SummerCountdownProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const leftItemRef = useRef<HTMLDivElement>(null);
  const leftShadowRef = useRef<HTMLDivElement>(null);
  const rightItemRef = useRef<HTMLDivElement>(null);
  const rightShadowRef = useRef<HTMLDivElement>(null);

  // 🌟 Proportional Shape Calculation States
  const [scaleFactor, setScaleFactor] = useState<number>(1);
  const [containerWidth, setContainerWidth] = useState<number>(1080);

  const [fetchedBanners, setFetchedBanners] = useState<BannerData[]>([]);
  const [bgCache, setBgCache] = useState<Record<string, string>>({});

  const getApiBase = () => {
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      if (host === "localhost" || host === "127.0.0.1" || host.includes("localhost")) {
        return "http://127.0.0.1:8000";
      }
      return "";
    }
    return "";
  };

  const resolveImg = (src?: string | null) => {
    if (!src) return "";
    if (src.startsWith("http://") || src.startsWith("https://") || src.startsWith("data:")) return src;
    const api = getApiBase();
    return `${api}${src.startsWith("/") ? "" : "/"}${src}`;
  };

  // 🌟 Screen Width onujayi Dynamic Scale & Height Calculator (2.25:1 aspect ratio lock)
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const currentWidth = containerRef.current.clientWidth;
        if (currentWidth > 0) {
          setContainerWidth(currentWidth);
          setScaleFactor(currentWidth / 1080);
        }
      }
    };

    updateDimensions();

    const resizeObserver = new ResizeObserver(() => {
      updateDimensions();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener("resize", updateDimensions);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateDimensions);
    };
  }, []);

  // Database theke active banners fetch
  useEffect(() => {
    const api = getApiBase();
    fetch(`${api}/api/v1/stores/banners/`)
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.results || [];
        const countdowns = list.filter((b: any) => b.banner_type === "countdown" && b.is_active !== false);
        if (countdowns.length > 0) {
          setFetchedBanners(countdowns);
        }
      })
      .catch(() => {});
  }, []);

  const bannerList = useMemo(() => {
    if (banners && banners.length > 0) return banners;
    if (fetchedBanners.length > 0) return fetchedBanners;
    if (banner) return [banner];
    return [];
  }, [banners, fetchedBanners, banner]);

  // Asset preloading for instant zero-lag switch
  useEffect(() => {
    if (bannerList.length === 0) return;
    const api = getApiBase();

    bannerList.forEach((b) => {
      if (b.image) {
        const pImg = new window.Image();
        pImg.src = resolveImg(b.image);
      }
      if (b.secondary_image) {
        const sImg = new window.Image();
        sImg.src = resolveImg(b.secondary_image);
      }

      if (b.background_image) {
        const url = resolveImg(b.background_image);
        const img = new window.Image();
        img.src = url;
        setBgCache((prev) => ({ ...prev, [url]: url }));
      } else {
        const preset = b.stage_preset || "blue_gold_showcase";
        fetch(`${api}/api/v1/ai/motion/stage-asset/?preset_id=${preset}`)
          .then((res) => res.json())
          .then((data) => {
            if (data.image) {
              const img = new window.Image();
              img.src = data.image;
              setBgCache((prev) => ({ ...prev, [preset]: data.image }));
            }
          })
          .catch(() => {
            const fallback = PRESET_BACKGROUNDS[preset] || "/banners/banner_blue_gold_showcase.png";
            setBgCache((prev) => ({ ...prev, [preset]: fallback }));
          });
      }
    });
  }, [bannerList]);

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex >= bannerList.length) {
      setCurrentIndex(0);
    }
  }, [bannerList.length, currentIndex]);

  const activeBanner = bannerList[currentIndex] || bannerList[0] || null;
  const [timeLeft, setTimeLeft] = useState({ days: 3, hours: 14, minutes: 28, seconds: 45 });

  const currentBgKey = activeBanner?.background_image
    ? resolveImg(activeBanner.background_image)
    : (activeBanner?.stage_preset || "blue_gold_showcase");

  const currentBg =
    bgCache[currentBgKey] ||
    (activeBanner?.background_image
      ? resolveImg(activeBanner.background_image)
      : PRESET_BACKGROUNDS[activeBanner?.stage_preset || "blue_gold_showcase"] || "");

  const handlePrevBanner = () => {
    if (bannerList.length <= 1) return;
    setCurrentIndex((prev) => (prev - 1 + bannerList.length) % bannerList.length);
  };

  const handleNextBanner = () => {
    if (bannerList.length <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % bannerList.length);
  };

  // Timer interval calculation
  useEffect(() => {
    if (!activeBanner?.countdown_end) {
      setTimeLeft({ days: 3, hours: 14, minutes: 28, seconds: 45 });
      return;
    }

    const calculateTime = () => {
      const targetTime = new Date(activeBanner.countdown_end!).getTime();
      const now = new Date().getTime();
      const diff = targetTime - now;

      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };

    calculateTime();
    const timer = setInterval(calculateTime, 1000);
    return () => clearInterval(timer);
  }, [activeBanner?.countdown_end, currentIndex]);

  // Floating animation
  useEffect(() => {
    const ctx = gsap.context(() => {
      if (leftItemRef.current && leftShadowRef.current) {
        const tlL = gsap.timeline({ repeat: -1, yoyo: true });
        tlL.to(leftItemRef.current, { y: -5, rotation: 1, duration: 2.8, ease: "sine.inOut" });
        tlL.to(leftShadowRef.current, { scaleX: 0.95, opacity: 0.75, duration: 2.8, ease: "sine.inOut" }, 0);
      }

      if (rightItemRef.current && rightShadowRef.current) {
        const tlR = gsap.timeline({ repeat: -1, yoyo: true, delay: 0.3 });
        tlR.to(rightItemRef.current, { y: -5, rotation: -1, duration: 3.0, ease: "sine.inOut" });
        tlR.to(rightShadowRef.current, { scaleX: 0.95, opacity: 0.75, duration: 3.0, ease: "sine.inOut" }, 0);
      }
    }, containerRef);

    return () => ctx.revert();
  }, [currentIndex]);

  if (!activeBanner) return null;

  const leftImg = resolveImg(activeBanner.image);
  const rightImg = resolveImg(activeBanner.secondary_image);

  let customTextList: CustomTextLayer[] = [];
  let btnMeta: any = null;

  if (activeBanner.custom_texts) {
    try {
      const raw = typeof activeBanner.custom_texts === "string" ? JSON.parse(activeBanner.custom_texts) : activeBanner.custom_texts;
      if (Array.isArray(raw)) {
        customTextList = raw.filter((item: any) => {
          if (item.id === "__btn_meta__") {
            btnMeta = item;
            return false;
          }
          return true;
        });
      }
    } catch {
      customTextList = [];
    }
  }

  // Exact coordinates from admin
  const lOx = Number(activeBanner.left_ox ?? 48);
  const lOy = Number(activeBanner.left_oy ?? -110);
  const lScale = Number(activeBanner.left_scale ?? 96);

  const rOx = Number(activeBanner.right_ox ?? -57);
  const rOy = Number(activeBanner.right_oy ?? 0);
  const rScale = Number(activeBanner.right_scale ?? 100);

  const textOx = Number(activeBanner.text_ox ?? 0);
  const textOy = Number(activeBanner.text_oy ?? -95);
  const textScale = Number(activeBanner.text_scale ?? 85);

  const timerOx = Number(activeBanner.timer_ox ?? 0);
  const timerOy = Number(activeBanner.timer_oy ?? 22);
  const timerScale = Number(activeBanner.timer_scale ?? 100);
  const timerGap = Number(activeBanner.timer_gap ?? 24);
  const timerPadX = Number(activeBanner.timer_pad_x ?? 17);
  const timerPadY = Number(activeBanner.timer_pad_y ?? 8);
  const isBoxBg = activeBanner.timer_box_bg === true || activeBanner.timer_box_bg === "true";

  const btnOx = Number(activeBanner.btn_ox ?? btnMeta?.x ?? -3);
  const btnOy = Number(activeBanner.btn_oy ?? btnMeta?.y ?? 139);
  const btnScale = Number(activeBanner.btn_scale ?? btnMeta?.scale ?? 100);
  const isBtnShow = btnMeta
    ? btnMeta.color === "true"
    : (activeBanner.btn_show !== false && activeBanner.btn_show !== "false");

  const buttonText = activeBanner.button_text || "CLAIM DEAL NOW";

  // Proportional Responsive Height (1080px × 480px)
  const calculatedHeight = containerWidth ? Math.round(containerWidth * (480 / 1080)) : 480;

  return (
    <section className="w-full max-w-7xl mx-auto px-2.5 sm:px-6 py-4 sm:py-6 font-sans select-none overflow-hidden">
      {/* 🌟 Responsive Parent Container */}
      <div
        ref={containerRef}
        style={{ height: `${calculatedHeight}px` }}
        className="relative w-full max-w-[1080px] mx-auto rounded-[18px] sm:rounded-[32px] overflow-hidden shadow-2xl border border-slate-200/80 bg-slate-950 flex items-center justify-center group"
      >
        {/* 🌟 1080x480 Virtual Vector Canvas: Sob element-er shape, pedestal o distance 100% lock thakbe */}
        <div
          style={{
            width: "1080px",
            height: "480px",
            transform: `scale(${scaleFactor})`,
            transformOrigin: "top left",
          }}
          className="absolute top-0 left-0 w-[1080px] h-[480px] pointer-events-auto shrink-0 select-none"
        >
          {/* 1. 3D Stage Background Plate */}
          {currentBg ? (
            <img
              key={currentBgKey}
              src={currentBg}
              alt={activeBanner.title || "3D Showroom Stage"}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-200"
            />
          ) : (
            <div className="absolute inset-0 bg-slate-950" />
          )}

          {/* 👟 2. Left Product Cutout */}
          {leftImg && (
            <div
              style={{ transform: `translate(${lOx}px, ${lOy}px) scale(${lScale / 100})` }}
              className="absolute left-[6%] bottom-[12%] flex flex-col items-center pointer-events-none z-20 transition-transform duration-75"
            >
              <div ref={leftShadowRef} className="w-44 h-8 rounded-full bg-slate-950/85 blur-sm" />
              <div className="absolute bottom-2 w-44 h-44 drop-shadow-[0_20px_25px_rgba(0,0,0,0.85)]">
                <div ref={leftItemRef} className="w-full h-full relative">
                  <Image src={leftImg} alt="Left Product" fill unoptimized priority className="object-contain" />
                </div>
              </div>
            </div>
          )}

          {/* ✍️ 3. Main Text Content: left-1/2 top-1/2 + calc(-50% + offset) diye exact center anchor kora hoyeche */}
          <div
            style={{
              transform: `translate(calc(-50% + ${textOx}px), calc(-50% + ${textOy}px)) scale(${textScale / 100})`,
            }}
            className="absolute left-1/2 top-1/2 z-30 max-w-sm w-[75%] p-2 text-center space-y-1 text-white pointer-events-none transition-transform duration-75 drop-shadow-[0_4px_14px_rgba(0,0,0,0.95)]"
          >
            <div className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-400/20 border border-amber-400/30 text-[9px] font-black text-amber-300">
              <Flame className="w-3 h-3 text-amber-400 animate-bounce" />
              <span>{activeBanner.badge_title || "⚡ 48H FLASH CLEARANCE"}</span>
            </div>

            <h2 className="text-sm sm:text-base md:text-lg font-black truncate text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]">
              {activeBanner.title || "আজকের সেরা ডিলে ৫০% পর্যন্ত ছাড়!"}
            </h2>
            <p className="text-[10px] text-slate-200 line-clamp-2 leading-relaxed drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]">
              {activeBanner.subtitle || "স্টক শেষ হওয়ার আগেই লুফে নিন আপনার পছন্দের অরিজিনাল পণ্য। সারা দেশে দ্রুত ক্যাশ অন ডেলিভারি!"}
            </p>
          </div>

          {/* ⏱️ 4. Countdown Timer: left-1/2 top-1/2 + calc(-50% + offset) diye exact center anchor kora hoyeche */}
          <div
            style={{
              transform: `translate(calc(-50% + ${timerOx}px), calc(-50% + ${timerOy}px)) scale(${timerScale / 100})`,
            }}
            className="absolute left-1/2 top-1/2 z-30 flex items-center justify-center pointer-events-none transition-transform duration-75"
          >
            <div
              style={{ gap: `${timerGap}px` }}
              className="flex items-center justify-center text-center font-mono"
            >
              {[
                { val: timeLeft.days, label: "DAYS" },
                { val: timeLeft.hours, label: "HOURS" },
                { val: timeLeft.minutes, label: "MINS" },
                { val: timeLeft.seconds, label: "SECS" },
              ].map((unit, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: `${timerPadY}px ${timerPadX}px`,
                  }}
                  className={`flex flex-col items-center justify-center transition-all ${
                    isBoxBg
                      ? "bg-black/50 border border-white/20 rounded-xl backdrop-blur-xs shadow-md"
                      : "bg-transparent drop-shadow-[0_2px_8px_rgba(0,0,0,0.95)]"
                  }`}
                >
                  <span className="text-sm sm:text-base md:text-lg font-black text-white leading-none tracking-tight">
                    {String(unit.val).padStart(2, "0")}
                  </span>
                  <span className="text-[8px] font-bold text-slate-300 mt-0.5 tracking-wider">
                    {unit.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* 🌟 5. CTA Button Layer: left-1/2 top-1/2 + calc(-50% + offset) */}
          {isBtnShow && buttonText && (
            <div
              style={{
                transform: `translate(calc(-50% + ${btnOx}px), calc(-50% + ${btnOy}px)) scale(${btnScale / 100})`,
              }}
              className="absolute left-1/2 top-1/2 z-35 pointer-events-auto transition-transform duration-75"
            >
              <Link
                href={activeBanner.target_url || "/products"}
                className="inline-flex items-center gap-2 px-8 py-2 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-black text-xs uppercase tracking-wider shadow-[0_4px_20px_rgba(37,99,235,0.4)] border border-cyan-400/30 cursor-pointer hover:scale-105 active:scale-95 transition-all"
              >
                <span>{buttonText}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          )}

          {/* 🌟 6. Dynamic Custom Text Layers (ORDER NOW): left-1/2 top-1/2 + calc(-50% + offset) */}
          {customTextList.map((ct) => (
            <div
              key={ct.id}
              style={{
                transform: `translate(calc(-50% + ${ct.x}px), calc(-50% + ${ct.y}px)) scale(${ct.scale / 100})`,
                color: ct.color,
                fontWeight: ct.weight === "black" ? 900 : ct.weight === "bold" ? 700 : 400,
              }}
              className="absolute left-1/2 top-1/2 z-35 pointer-events-none text-center drop-shadow-[0_3px_10px_rgba(0,0,0,0.95)] tracking-wider whitespace-nowrap transition-transform duration-75"
            >
              {ct.text}
            </div>
          ))}

          {/* 🧥 7. Right Model / Product Cutout */}
          {rightImg && (
            <div
              style={{ transform: `translate(${rOx}px, ${rOy}px) scale(${rScale / 100})` }}
              className="absolute right-[6%] bottom-[12%] flex flex-col items-center pointer-events-none z-20 transition-transform duration-75"
            >
              <div ref={rightShadowRef} className="w-44 h-8 rounded-full bg-slate-950/85 blur-sm" />
              <div className="absolute bottom-2 w-44 h-44 drop-shadow-[0_20px_25px_rgba(0,0,0,0.85)]">
                <div ref={rightItemRef} className="w-full h-full relative">
                  <Image src={rightImg} alt="Right Promo" fill unoptimized priority className="object-contain" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 🌟 8. Touch-friendly Navigation Controls (Outer container-e fixed, jate mobile-e clickable thake) */}
        {bannerList.length > 1 && (
          <button
            type="button"
            onClick={handlePrevBanner}
            className="absolute left-2 sm:left-3.5 top-1/2 -translate-y-1/2 z-40 p-2 sm:p-2.5 rounded-full bg-slate-950/75 border border-white/20 text-white hover:bg-purple-600 hover:scale-110 active:scale-95 transition-all cursor-pointer backdrop-blur-md shadow-xl opacity-80 hover:opacity-100"
            title="Previous Banner"
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        )}

        {bannerList.length > 1 && (
          <button
            type="button"
            onClick={handleNextBanner}
            className="absolute right-2 sm:right-3.5 top-1/2 -translate-y-1/2 z-40 p-2 sm:p-2.5 rounded-full bg-slate-950/75 border border-white/20 text-white hover:bg-purple-600 hover:scale-110 active:scale-95 transition-all cursor-pointer backdrop-blur-md shadow-xl opacity-80 hover:opacity-100"
            title="Next Banner"
          >
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        )}

        {bannerList.length > 1 && (
          <div className="absolute bottom-2 sm:bottom-3 left-1/2 -translate-x-1/2 z-40 flex items-center gap-1.5 p-1 px-2.5 rounded-full bg-slate-950/75 backdrop-blur-md border border-white/10 shadow-lg">
            {bannerList.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setCurrentIndex(i)}
                className={`h-1.5 sm:h-2 rounded-full transition-all cursor-pointer ${
                  currentIndex === i ? "w-5 sm:w-6 bg-purple-500" : "w-1.5 sm:w-2 bg-white/40 hover:bg-white/70"
                }`}
                title={`Banner ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}